import { FC, ReactNode } from 'react';
import { DATA_TABLE_ALL_PAGE_SIZE } from '../dataTableState';
import Pagination from '../../atoms/Pagination';

interface DataTablePagerProps {
    /** Текущая страница, 0-based. */
    page: number;
    pageSize: number;
    totalCount: number;
    pagerSizes: number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    /** Префикс классов футера: `dtb` у таблицы, `atb` у грида вложений. */
    classPrefix?: string;
    'data-testid'?: string;
    showPageSizeSelector?: boolean;
    showInfo?: boolean;
    /** Итог вместо «всего N» (при серверной группировке — групп и строк). */
    totalText?: string;
    /** Правая часть футера (footerEnd таблицы). Видна и без пейджера, когда всё
     *  помещается на одну страницу. */
    end?: ReactNode;
}

const DataTablePager: FC<DataTablePagerProps> = ({
    page,
    pageSize,
    totalCount,
    pagerSizes,
    onPageChange,
    onPageSizeChange,
    classPrefix = 'dtb',
    'data-testid': testId,
    showPageSizeSelector = true,
    showInfo = true,
    totalText,
    end,
}) => {
    const endSlot = end ? <div className={`${classPrefix}__foot-end`}>{end}</div> : null;
    // Всё влезает на одну страницу даже при минимальном размере — пейджер бесполезен.
    // Фактический pageSize может быть меньше любой кнопки (конфигный размер):
    // тогда страниц больше одной и пейджер обязан остаться.
    const minPagerSize = pagerSizes.length ? Math.min(...pagerSizes) : pageSize;
    const pageCount = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
    const singlePage = pageCount <= 1;
    // Без селектора размера на единственной странице в футере не остаётся ничего полезного.
    if (totalCount <= Math.min(minPagerSize, pageSize) || (singlePage && !showPageSizeSelector))
        return endSlot && <div className={`${classPrefix}__foot`}>{endSlot}</div>;

    const current = Math.min(Math.max(0, page), pageCount - 1);
    // «Все» активна, только если размер не совпал ни с одной кнопкой и покрывает все строки.
    const allActive = totalCount > 0 && pageSize >= totalCount && !pagerSizes.includes(pageSize);

    return (
        <div className={`${classPrefix}__foot`} data-testid={testId}>
            {showPageSizeSelector ? (
                <div className={`${classPrefix}__pagesize`}>
                    {pagerSizes.map((size) => (
                        <button
                            key={size}
                            type="button"
                            className={!allActive && size === pageSize ? 'is-active' : undefined}
                            onClick={() => onPageSizeChange(size)}
                        >
                            {size}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={allActive ? 'is-active' : undefined}
                        // «Все» — липкий режим: огромный размер, а не текущий totalCount,
                        // иначе добавленная строка рождала бы вторую страницу
                        onClick={() => onPageSizeChange(DATA_TABLE_ALL_PAGE_SIZE)}
                    >
                        Все
                    </button>
                </div>
            ) : (
                <span />
            )}

            {showInfo && (
                <span className={`${classPrefix}__foot-info`}>
                    {singlePage
                        ? totalText
                            ? totalText.charAt(0).toUpperCase() + totalText.slice(1)
                            : `Всего ${totalCount}`
                        : `Страница ${current + 1} из ${pageCount} · ${totalText ?? `всего ${totalCount}`}`}
                </span>
            )}

            {endSlot}

            {!singlePage && (
                <Pagination
                    classPrefix={classPrefix}
                    value={current + 1}
                    total={pageCount}
                    onChange={(p) => onPageChange(p - 1)}
                    previousLabel="Назад"
                    nextLabel="Вперёд"
                    pageLabel={null}
                />
            )}
        </div>
    );
};

export default DataTablePager;
